import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, FileText, Search, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EditRegDialog, DeleteRegDialog, DeletedList } from "./MarathonRegActions";
import { attemptLabel, attemptTone } from "@/pages/public/run/runShared";

const AttemptBadge = ({ status, testid }) => status ? <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-sm whitespace-nowrap ${attemptTone(status)}`} data-testid={testid}>{attemptLabel(status)}</span> : null;

const inputCls = "bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333]";
const STATUS_TONE = { PAID: "bg-[#e6efe9] text-[#234333]", PENDING: "bg-amber-50 text-amber-800", FAILED: "bg-red-50 text-red-700", CANCELLED: "bg-gray-100 text-gray-700" };

async function openBlob(url, filename, download) {
  const res = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(res.data);
  if (download) { const a = document.createElement("a"); a.href = href; a.download = filename; a.click(); }
  else window.open(href, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(href), 60000);
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm p-4">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">{label}</div>
      <div className="font-serif text-3xl text-[#1c1a17] mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}

function DetailDialog({ regId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (regId) api.get(`/admin/marathon/registrations/${regId}`).then(({ data }) => setD(data)); else setD(null); }, [regId]);
  const rows = d ? [
    ["Registration No.", d.registration_no], ["Bib", d.bib_number], ["Run", `${d.run_name} (${d.distance_km} KM)`], ["Event", d.event_date],
    ["Name", d.full_name], ["Gender", d.gender], ["DOB / Age", `${d.date_of_birth} / ${d.age_on_event}`], ["Mobile", d.mobile], ["Email", d.email],
    ["City", d.city], ["Address", d.address], ["T-shirt", d.tshirt_size], ["Blood group", d.blood_group],
    ["Emergency", `${d.emergency_contact_name} · ${d.emergency_contact_phone}`], ["Medical", d.medical_conditions], ["Club", d.running_club],
    ["Best time", d.previous_best_time], ["Fee", `₹${d.fee}`], ["Status", d.payment_status],
  ] : [];
  return (
    <Dialog open={!!regId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mreg-detail-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{d?.full_name || "Loading…"}</DialogTitle></DialogHeader>
        {d && (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {rows.map(([k, v]) => (<div key={k}><div className="text-[10px] uppercase tracking-wider text-[#6b655b]">{k}</div><div className="break-words">{v || "—"}</div></div>))}
            </div>
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => openBlob(`/admin/marathon/registrations/${d.id}/aadhaar`).catch(() => toast.error("Could not open Aadhaar"))} className="inline-flex items-center gap-1.5 border border-[#234333] text-[#234333] px-3 py-2 text-xs font-semibold rounded-sm" data-testid="mreg-view-aadhaar"><Eye size={13} />View Aadhaar</button>
              {d.payment_status === "PAID" && (
                <button type="button" onClick={() => openBlob(`/admin/marathon/registrations/${d.id}/receipt`, `Receipt-${d.registration_no}.pdf`, true).catch(() => toast.error("Receipt unavailable"))} className="inline-flex items-center gap-1.5 bg-[#234333] text-white px-3 py-2 text-xs font-semibold rounded-sm" data-testid="mreg-download-receipt"><FileText size={13} />Receipt PDF</button>
              )}
              {d.payment_status === "PAID" && (
                <button type="button" onClick={async () => {
                  const { data } = await api.post(`/admin/marathon/registrations/${d.id}/whatsapp`);
                  if (data.status === "SENT") toast.success("WhatsApp confirmation sent");
                  else toast.error(`WhatsApp ${data.status.toLowerCase()}: ${data.error || ""}`);
                }} className="inline-flex items-center gap-1.5 border border-[#25D366] text-[#128C7E] px-3 py-2 text-xs font-semibold rounded-sm" data-testid="mreg-send-whatsapp"><MessageCircle size={13} />Send WhatsApp</button>
              )}
            </div>
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6b655b] font-semibold mb-2">Payment attempts</div>
              <table className="w-full text-xs border border-[#e5e0d8]" data-testid="mreg-attempts">
                <thead className="bg-[#f9f8f6]"><tr>{["Order ID", "Started", "Amount", "Status", "Tracking", "Reason"].map((h) => <th key={h} className="text-left p-2">{h}</th>)}</tr></thead>
                <tbody>
                  {d.payment_attempts.length === 0 && <tr><td colSpan={6} className="p-2 text-[#6b655b]">No attempts yet</td></tr>}
                  {d.payment_attempts.map((o) => (
                    <tr key={o.order_id} className="border-t border-[#e5e0d8]"><td className="p-2 font-mono">{o.order_id}</td><td className="p-2 whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td><td className="p-2">₹{o.amount}</td><td className="p-2"><AttemptBadge status={o.attempt_status} /></td><td className="p-2">{o.tracking_id || "—"}</td><td className="p-2">{o.failure_reason || "—"}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MarathonRegistrations() {
  const [runs, setRuns] = useState([]);
  const [filters, setFilters] = useState({ run_id: "", status: "", gender: "", q: "" });
  const [data, setData] = useState({ items: [], stats: { total: 0, paid: 0, pending: 0, revenue: 0 } });
  const [detail, setDetail] = useState("");
  const [editReg, setEditReg] = useState(null);
  const [delReg, setDelReg] = useState(null);
  const [view, setView] = useState("active");

  useEffect(() => { api.get("/admin/marathon/runs").then(({ data }) => setRuns(data)); }, []);
  const load = useCallback(() => api.get("/admin/marathon/registrations", { params: filters }).then(({ data }) => setData(data)), [filters]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/marathon/registrations/export.csv", { params: filters, responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = href; a.download = "run-registrations.csv"; a.click(); URL.revokeObjectURL(href);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-marathon-registrations">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Marathon</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Run Registrations</h1>
        </div>
        <button type="button" onClick={exportCsv} className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm" data-testid="mreg-export-csv"><Download size={15} />Export CSV</button>
      </div>
      <div className="flex gap-1 border-b border-[#e5e0d8]">
        {[["active", "Registrations"], ["deleted", "Deleted archive"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setView(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${view === k ? "border-[#234333] text-[#234333]" : "border-transparent text-[#6b655b]"}`} data-testid={`mreg-tab-${k}`}>{l}</button>
        ))}
      </div>
      {view === "deleted" ? <DeletedList /> : (<>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Registrations" value={data.stats.total} testid="mreg-stat-total" />
        <Stat label="Paid" value={data.stats.paid} testid="mreg-stat-paid" />
        <Stat label="Unpaid" value={data.stats.pending} testid="mreg-stat-pending" />
        <Stat label="Revenue" value={`₹${Number(data.stats.revenue).toLocaleString("en-IN")}`} testid="mreg-stat-revenue" />
      </div>
      <div className="flex flex-wrap gap-2 items-center bg-white border border-[#e5e0d8] rounded-sm p-3">
        <div className="relative flex-1 min-w-[220px]"><Search size={14} className="absolute left-3 top-2.5 text-[#8a8478]" /><input className={`${inputCls} w-full pl-8`} placeholder="Name, mobile, email, reg no, bib…" value={filters.q} onChange={set("q")} data-testid="mreg-search" /></div>
        <select className={inputCls} value={filters.run_id} onChange={set("run_id")} data-testid="mreg-filter-run"><option value="">All runs</option>{runs.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        <select className={inputCls} value={filters.status} onChange={set("status")} data-testid="mreg-filter-status"><option value="">All statuses</option>{["PAID", "PENDING", "FAILED", "CANCELLED"].map((s) => <option key={s}>{s}</option>)}</select>
        <select className={inputCls} value={filters.gender} onChange={set("gender")} data-testid="mreg-filter-gender"><option value="">All genders</option>{["Male", "Female", "Other"].map((s) => <option key={s}>{s}</option>)}</select>
      </div>
      <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="mreg-table">
          <thead className="bg-[#f9f8f6] text-[10px] uppercase tracking-wider text-[#6b655b]">
            <tr>{["Reg No.", "Bib", "Name", "Run", "Gender / Age", "Mobile", "T-shirt", "Fee", "Status", "Last Order ID", "Attempts", ""].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={12} className="p-8 text-center text-[#6b655b]" data-testid="mreg-empty">No registrations found</td></tr>}
            {data.items.map((r) => (
              <tr key={r.id} className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]" data-testid={`mreg-row-${r.id}`}>
                <td className="p-3 font-mono text-xs">{r.registration_no}</td>
                <td className="p-3 font-semibold">{r.bib_number || "—"}</td>
                <td className="p-3">{r.full_name}<div className="text-[11px] text-[#6b655b]">{r.email}</div></td>
                <td className="p-3">{r.distance_km} KM</td>
                <td className="p-3">{r.gender} / {r.age_on_event}</td>
                <td className="p-3">{r.mobile}</td>
                <td className="p-3">{r.tshirt_size}</td>
                <td className="p-3">₹{r.fee}</td>
                <td className="p-3"><span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-sm ${STATUS_TONE[r.payment_status] || "bg-gray-100"}`}>{r.payment_status}</span></td>
                <td className="p-3" data-testid={`mreg-last-order-${r.id}`}>{r.last_order_id ? <><div className="font-mono text-[11px] whitespace-nowrap">{r.last_order_id}</div><AttemptBadge status={r.last_order_status} testid={`mreg-last-order-status-${r.id}`} /></> : <span className="text-[#8a8478] text-xs">No attempt</span>}</td>
                <td className="p-3 text-center font-semibold" data-testid={`mreg-attempts-${r.id}`}>{r.attempt_count}</td>
                <td className="p-3 whitespace-nowrap space-x-3">
                  <button type="button" onClick={() => setDetail(r.id)} className="text-[#234333] text-xs font-semibold hover:underline" data-testid={`mreg-view-${r.id}`}>View</button>
                  <button type="button" onClick={() => setEditReg(r)} className="text-[#234333] text-xs font-semibold hover:underline" data-testid={`mreg-edit-${r.id}`}>Edit</button>
                  <button type="button" onClick={() => setDelReg(r)} className="text-red-700 text-xs font-semibold hover:underline" data-testid={`mreg-delete-${r.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>)}
      <DetailDialog regId={detail} onClose={() => setDetail("")} />
      <EditRegDialog reg={editReg} onClose={() => setEditReg(null)} onSaved={() => { setEditReg(null); load(); }} />
      <DeleteRegDialog reg={delReg} onClose={() => setDelReg(null)} onDeleted={() => { setDelReg(null); load(); }} />
    </div>
  );
}
